import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    FunnelIcon,
    BellAlertIcon,
    EnvelopeIcon,
    PhoneIcon,
    BriefcaseIcon,
    ShieldCheckIcon,
    Squares2X2Icon,
    LinkIcon,
    ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import { useAuth } from '../contexts/AuthContext';

interface User {
    id: number;
    full_name: string;
    email: string;
    role: string;
    is_active: boolean;
    created_at: string;
    last_login?: string;
    phone?: string;
    farmer_id?: number | null;
    farmer_name?: string | null;
    farmer_code?: string | null;
    page_access?: string[];
    global_role?: string;
    membership_role?: string;
    membership_status?: string;
    membership_tenant_id?: number | null;
    primary_tenant_id?: number | null;
    updated_at?: string;
}

interface PendingUser {
    id: number;
    full_name: string;
    email: string;
    phone?: string;
    requested_role: string;
    registration_notes?: string;
    registration_date: string;
    has_password?: boolean;
}

interface UserStats {
    total: number;
    active: number;
    inactive: number;
    pending: number;
    byRole: {
        [key: string]: number;
    };
}

const STAFF_ROLE_OPTIONS = [
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'agronomist', label: 'Agronomist' },
    { value: 'field_facilitator', label: 'Field Facilitator' },
    { value: 'farmer', label: 'Farmer' }
] as const;

const STAFF_ROLE_SET = new Set(STAFF_ROLE_OPTIONS.map((role) => role.value));
const ROLE_PAGE_ALLOWLISTS: Record<string, string[] | null> = {
    project_manager: null,
    agronomist: ['dashboard', 'farmers', 'crops', 'traceability', 'tree-inspections', 'activities', 'field-diary', 'issues', 'maps', 'training', 'guidance', 'compost', 'farm-finance'],
    field_facilitator: ['dashboard', 'farmers', 'activities', 'field-diary', 'issues', 'maps', 'training', 'vsla', 'tree-inspections'],
    farmer: ['dashboard', 'traceability', 'activities', 'field-diary', 'issues', 'training', 'vsla', 'maps', 'guidance'],
};
const sanitizeStaffRole = (role?: string): typeof STAFF_ROLE_OPTIONS[number]['value'] => {
    const normalized = (role || '').toLowerCase();
    return STAFF_ROLE_SET.has(normalized as typeof STAFF_ROLE_OPTIONS[number]['value'])
        ? (normalized as typeof STAFF_ROLE_OPTIONS[number]['value'])
        : 'field_facilitator';
};

const roleCanAccessPage = (role: string, pageKey: string) => {
    const normalized = sanitizeStaffRole(role);
    const allowed = Object.prototype.hasOwnProperty.call(ROLE_PAGE_ALLOWLISTS, normalized)
        ? ROLE_PAGE_ALLOWLISTS[normalized]
        : ['dashboard'];
    return allowed === null || allowed.includes(pageKey);
};

const APPROVAL_ROLE_OPTIONS = [
    ...STAFF_ROLE_OPTIONS
] as const;

const APPROVAL_ROLE_SET = new Set(APPROVAL_ROLE_OPTIONS.map((role) => role.value));
const sanitizeApprovalRole = (role?: string): typeof APPROVAL_ROLE_OPTIONS[number]['value'] => {
    const normalized = (role || '').toLowerCase();
    return APPROVAL_ROLE_SET.has(normalized as typeof APPROVAL_ROLE_OPTIONS[number]['value'])
        ? (normalized as typeof APPROVAL_ROLE_OPTIONS[number]['value'])
        : 'field_facilitator';
};

const UsersPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [activeTab, setActiveTab] = useState<'users' | 'pending' | 'activity'>('users');

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [selectedPendingUser, setSelectedPendingUser] = useState<PendingUser | null>(null);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        role: 'field_facilitator',
        is_active: true
    });
    const [selectedPageKeys, setSelectedPageKeys] = useState<string[]>([]);
    const [inviteLinkData, setInviteLinkData] = useState({
        role: 'field_facilitator',
        page_access: [] as string[],
    });
    const [generatedInviteUrl, setGeneratedInviteUrl] = useState('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);

    const [approvalData, setApprovalData] = useState({
        role: 'field_facilitator'
    });

    const [rejectionReason, setRejectionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchUsers();
        fetchPendingUsers();
    }, []);

    const availablePages = React.useMemo(() => {
        const pageMap = currentUser?.tenant_pages || {};
        return Object.entries(pageMap)
            .map(([key, entry]: [string, any]) => ({
                key,
                title: entry?.title || key.replace(/-/g, ' ').replace(/_/g, ' '),
                sort_order: Number(entry?.sort_order ?? 100),
                enabled: entry?.enabled !== false && entry?.is_enabled !== false,
            }))
            .filter((page) => page.enabled)
            .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
    }, [currentUser?.tenant_pages]);

    const defaultPageKeys = React.useMemo(
        () => availablePages.map((page) => page.key),
        [availablePages]
    );

    const rolePageDefaults = React.useCallback((role: string) => {
        const normalized = sanitizeStaffRole(role);
        return defaultPageKeys.filter((key) => roleCanAccessPage(normalized, key));
    }, [defaultPageKeys]);

    const roleAvailablePages = React.useMemo(() => {
        return availablePages.filter((page) => roleCanAccessPage(formData.role, page.key));
    }, [availablePages, formData.role]);

    const inviteAvailablePages = React.useMemo(
        () => availablePages.filter((page) => roleCanAccessPage(inviteLinkData.role, page.key)),
        [availablePages, inviteLinkData.role]
    );

    const openInviteModal = () => {
        setInviteLinkData({ role: 'field_facilitator', page_access: rolePageDefaults('field_facilitator') });
        setGeneratedInviteUrl('');
        setShowInviteLinkModal(true);
    };

    const openCreateUserModal = () => {
        setEditingUser(null);
        setFormData({ full_name: '', email: '', phone: '', role: 'field_facilitator', is_active: true });
        setSelectedPageKeys(rolePageDefaults('field_facilitator'));
        setShowModal(true);
    };

    const toggleInvitePageAccess = (pageKey: string) => {
        setInviteLinkData((prev) => ({
            ...prev,
            page_access: prev.page_access.includes(pageKey)
                ? prev.page_access.filter((key) => key !== pageKey)
                : [...prev.page_access, pageKey],
        }));
    };

    const handleCreateInviteLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setInviteSubmitting(true);
        setGeneratedInviteUrl('');
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/users/invite-link`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    role: sanitizeStaffRole(inviteLinkData.role),
                    page_access: inviteLinkData.page_access,
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.message || 'Failed to create invite link');
            setGeneratedInviteUrl(data.registration_url);
        } catch (error: any) {
            alert(error.message || 'Failed to create invite link');
        } finally {
            setInviteSubmitting(false);
        }
    };

    const fetchUsers = async () => {
        setFetchError(null);
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/users`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || data.message || `Failed to fetch users (${response.status})`);
            }
            const data = await response.json();
            setUsers(data);
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setUsers([]);
            setFetchError(error?.message || 'Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingUsers = async () => {
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/users/pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setPendingUsers(data);
            }
        } catch (error) {
            console.error('Error fetching pending users:', error);
        }
    };

    const handleApprove = async () => {
        if (!selectedPendingUser) return;
        setActionLoading(true);

        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(
                `${API_URL}/api/users/${selectedPendingUser.id}/approve`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(approvalData)
                }
            );

            if (response.ok) {
                await fetchUsers();
                await fetchPendingUsers();
                setShowApproveModal(false);
                setSelectedPendingUser(null);
                setApprovalData({ role: 'field_facilitator' });
                alert('User approved successfully!');
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to approve user');
            }
        } catch (error) {
            console.error('Error approving user:', error);
            alert('Failed to approve user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedPendingUser) return;
        setActionLoading(true);

        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(
                `${API_URL}/api/users/${selectedPendingUser.id}/reject`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ reason: rejectionReason })
                }
            );

            if (response.ok) {
                await fetchPendingUsers();
                setShowRejectModal(false);
                setSelectedPendingUser(null);
                setRejectionReason('');
                alert('User registration rejected');
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to reject user');
            }
        } catch (error) {
            console.error('Error rejecting user:', error);
            alert('Failed to reject user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('aaywa_token');
            const url = editingUser
                ? `${API_URL}/api/users/${editingUser.id}`
                : `${API_URL}/api/users`;

            const method = editingUser ? 'PATCH' : 'POST';
            const payload = editingUser
                ? {
                    full_name: formData.full_name,
                    email: formData.email || undefined,
                    phone: formData.phone || undefined,
                    role: sanitizeStaffRole(formData.role),
                    is_active: formData.is_active,
                    page_access: selectedPageKeys
                }
                : { ...formData, role: sanitizeStaffRole(formData.role), page_access: selectedPageKeys };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const responseData = await response.json().catch(() => ({}));

            if (response.ok) {
                await fetchUsers();
                setShowModal(false);
                setEditingUser(null);
                setFormData({ full_name: '', email: '', phone: '', role: 'field_facilitator', is_active: true });
                setSelectedPageKeys(rolePageDefaults('field_facilitator'));
                if (!editingUser && sanitizeStaffRole(formData.role) === 'farmer') {
                    alert('Farmer created. They can sign in to the mobile app using this phone number only.');
                } else if (!editingUser && sanitizeStaffRole(formData.role) !== 'farmer') {
                    alert(responseData.email_sent
                        ? 'User created and OTP login email sent.'
                        : 'User created. Email was not sent, so please check SMTP logs.');
                }
            } else {
                alert(responseData.error || responseData.message || 'Failed to save user');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            alert('Failed to save user');
        }
    };

    const handleDelete = async (userId: number) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchUsers();
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setFormData({
            full_name: user.full_name,
            email: user.email,
            phone: (user as any).phone || '',
            role: sanitizeStaffRole(user.role),
            is_active: user.is_active
        });
        const role = sanitizeStaffRole(user.role);
        const selected = user.page_access && user.page_access.length > 0
            ? user.page_access
            : defaultPageKeys;
        setSelectedPageKeys(selected.filter((key) => roleCanAccessPage(role, key)));
        setShowModal(true);
    };

    const openApproveModal = (user: PendingUser) => {
        setSelectedPendingUser(user);
        setApprovalData({
            role: sanitizeApprovalRole(user.requested_role)
        });
        setShowApproveModal(true);
    };

    const openRejectModal = (user: PendingUser) => {
        setSelectedPendingUser(user);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    // Calculate statistics
    const stats: UserStats = React.useMemo(() => {
        const total = users.length;
        const active = users.filter(u => u.is_active).length;
        const inactive = total - active;
        const pending = pendingUsers.length;
        const byRole = users.reduce((acc, user) => {
            acc[user.role] = (acc[user.role] || 0) + 1;
            return acc;
        }, {} as { [key: string]: number });

        return { total, active, inactive, pending, byRole };
    }, [users, pendingUsers]);

    // Filter users
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.phone || '').includes(searchTerm);
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && user.is_active) ||
            (statusFilter === 'inactive' && !user.is_active);

        return matchesSearch && matchesRole && matchesStatus;
    });

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'project_manager': return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'agronomist': return 'bg-green-100 text-green-800 border-green-200';
            case 'field_facilitator': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'farmer': return 'bg-amber-100 text-amber-800 border-amber-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getRoleIcon = (role: string) => {
        const iconClass = "w-4 h-4 mr-1.5";
        return <UserGroupIcon className={iconClass} />;
    };

    const formatRole = (role: string) => role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString() : 'Not recorded';
    const togglePageAccess = (pageKey: string) => {
        setSelectedPageKeys((prev) =>
            prev.includes(pageKey)
                ? prev.filter((key) => key !== pageKey)
                : [...prev, pageKey]
        );
    };

    const activityItems = React.useMemo(() => {
        const pending = pendingUsers.map((user) => ({
            id: `pending-${user.id}`,
            title: `${user.full_name} requested access`,
            detail: `${formatRole(user.requested_role)} - ${user.email}`,
            date: user.registration_date,
            tone: 'orange',
            badge: 'OTP access'
        }));

        const joined = users.slice(0, 12).map((user) => ({
            id: `user-${user.id}`,
            title: `${user.full_name} is ${user.is_active ? 'active' : 'inactive'}`,
            detail: `${formatRole(user.role)} - ${user.email}`,
            date: user.created_at,
            tone: user.is_active ? 'emerald' : 'red',
            badge: user.last_login ? `Last login ${formatDate(user.last_login)}` : 'No login yet'
        }));

        return [...pending, ...joined]
            .sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())
            .slice(0, 14);
    }, [pendingUsers, users]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                        User Management
                    </h1>
                    <p className="text-gray-600 mt-1">Manage tenant staff under the Project Manager. Platform-owner roles are handled in Super Admin.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={openInviteModal}
                        className="group bg-white border-2 border-emerald-500 text-emerald-600 px-6 py-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-300"
                    >
                        <LinkIcon className="w-5 h-5" />
                        <span className="font-semibold">Create Invite Link</span>
                    </button>
                    <button
                        onClick={openCreateUserModal}
                        className="group bg-gradient-to-r from-emerald-500 to-cyan-600 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-cyan-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                        <PlusIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        <span className="font-semibold">Create User</span>
                    </button>
                </div>
            </div>

            {fetchError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
                    <p className="font-black">Users could not be loaded</p>
                    <p className="mt-1 text-red-600">{fetchError}</p>
                    <button
                        type="button"
                        onClick={() => {
                            setLoading(true);
                            fetchUsers();
                        }}
                        className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-xs font-black text-white hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-2 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('users')}
                    className={`px-6 py-3 font-semibold transition-all relative ${activeTab === 'users'
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Active Users
                    <span className="ml-2 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full">
                        {stats.total}
                    </span>
                </button>
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-6 py-3 font-semibold transition-all relative ${activeTab === 'pending'
                        ? 'text-orange-600 border-b-2 border-orange-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Pending Activation
                    {stats.pending > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full animate-pulse">
                            {stats.pending}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('activity')}
                    className={`px-6 py-3 font-semibold transition-all relative ${activeTab === 'activity'
                        ? 'text-slate-700 border-b-2 border-slate-700'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Activity
                    <span className="ml-2 px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded-full">
                        {activityItems.length}
                    </span>
                </button>
            </div>

            {/* Pending Approvals Tab */}
            {activeTab === 'pending' && (
                <div className="space-y-6">
                    {/* Pending Stats */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border-2 border-orange-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-orange-600 text-sm font-semibold uppercase tracking-wide">Requests sent</p>
                                <p className="text-4xl font-bold text-orange-900 mt-2">{stats.pending}</p>
                                <p className="text-sm text-orange-700 mt-1">Waiting for activation approval</p>
                            </div>
                            <div className="bg-orange-500 p-4 rounded-xl">
                                <BellAlertIcon className="w-8 h-8 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Pending Users Grid */}
                    {pendingUsers.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
                            <CheckCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-700 mb-2">No Pending Activation</h3>
                            <p className="text-gray-500">All invited registration requests have been activated or processed</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {pendingUsers.map((user) => (
                                <div
                                    key={user.id}
                                    className="bg-white rounded-2xl shadow-lg border-2 border-orange-200 p-6 hover:shadow-xl transition-shadow"
                                >
                                    {/* User Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                                                {user.full_name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900">{user.full_name}</h3>
                                                <p className="text-sm text-gray-500 flex items-center">
                                                    <ClockIcon className="w-3 h-3 mr-1" />
                                                    {new Date(user.registration_date).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded-full border-2 border-orange-200">
                                            WAITING ACTIVATION
                                        </span>
                                    </div>

                                    {/* User Details */}
                                    <div className="space-y-3 mb-4">
                                        <div className="flex items-center text-sm">
                                            <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                                            <span className="text-gray-700">{user.email}</span>
                                        </div>
                                        {user.phone && (
                                            <div className="flex items-center text-sm">
                                                <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
                                                <span className="text-gray-700">{user.phone}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center text-sm">
                                            <BriefcaseIcon className="w-4 h-4 text-gray-400 mr-2" />
                                            <span className={`px-3 py-1 text-xs font-bold rounded-full capitalize ${getRoleBadgeColor(user.requested_role)}`}>
                                                {user.requested_role.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <div className="flex items-center text-sm p-3 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-800">
                                            <ShieldCheckIcon className="w-4 h-4 flex-shrink-0" />
                                            <span className="ml-2 font-semibold">
                                                OTP-first access. No password required.
                                            </span>
                                        </div>
                                        <div className="flex items-center text-sm p-3 rounded-xl border bg-orange-50 border-orange-200 text-orange-800">
                                            <ClockIcon className="w-4 h-4 flex-shrink-0" />
                                            <span className="ml-2 font-semibold">
                                                Request sent. Waiting for project manager activation.
                                            </span>
                                        </div>
                                        {user.registration_notes && (
                                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                                <p className="text-xs text-gray-500 font-semibold mb-1">Message:</p>
                                                <p className="text-sm text-gray-700 italic">"{user.registration_notes}"</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={() => openApproveModal(user)}
                                            className="flex-1 py-2.5 px-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                                        >
                                            <CheckCircleIcon className="w-5 h-5" />
                                            <span>Approve</span>
                                        </button>
                                        <button
                                            onClick={() => openRejectModal(user)}
                                            className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-colors shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                                        >
                                            <XCircleIcon className="w-5 h-5" />
                                            <span>Reject</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 bg-slate-50">
                            <h2 className="text-xl font-bold text-slate-900">Account Activity</h2>
                            <p className="text-sm text-slate-500 mt-1">Recent requests, account status, and login readiness.</p>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {activityItems.length === 0 ? (
                                <div className="p-10 text-center">
                                    <ClockIcon className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                                    <p className="font-semibold text-gray-600">No activity yet</p>
                                </div>
                            ) : (
                                activityItems.map((item) => (
                                    <div key={item.id} className="p-5 flex items-start gap-4 hover:bg-slate-50 transition-colors">
                                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${item.tone === 'orange'
                                            ? 'bg-orange-100 text-orange-700'
                                            : item.tone === 'red'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                            {item.tone === 'orange' ? <BellAlertIcon className="w-5 h-5" /> : <UserGroupIcon className="w-5 h-5" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                <h3 className="font-bold text-slate-900 truncate">{item.title}</h3>
                                                <span className="text-xs font-semibold text-slate-500">{formatDate(item.date)}</span>
                                            </div>
                                            <p className="text-sm text-slate-600 mt-1 truncate">{item.detail}</p>
                                            <span className="inline-flex mt-3 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                                                {item.badge}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-6 shadow-xl">
                            <p className="text-sm uppercase tracking-wide text-slate-300 font-semibold">Access Health</p>
                            <p className="text-4xl font-black mt-3">{stats.active}/{stats.total}</p>
                            <p className="text-sm text-slate-300 mt-1">active approved accounts</p>
                        </div>
                        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                            <p className="font-bold text-gray-900 mb-4">Role Coverage</p>
                            <div className="space-y-3">
                                {Object.entries(stats.byRole).length === 0 ? (
                                    <p className="text-sm text-gray-500">No users yet.</p>
                                ) : (
                                    Object.entries(stats.byRole).map(([role, count]) => (
                                        <div key={role} className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">{formatRole(role)}</span>
                                            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">{count}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Users Tab */}
            {activeTab === 'users' && (
                <>
                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Users */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide">Total Users</p>
                                    <p className="text-4xl font-bold text-blue-900 mt-2">{stats.total}</p>
                                </div>
                                <div className="bg-blue-500 p-4 rounded-xl">
                                    <UserGroupIcon className="w-8 h-8 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Active Users */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-600 text-sm font-semibold uppercase tracking-wide">Active</p>
                                    <p className="text-4xl font-bold text-green-900 mt-2">{stats.active}</p>
                                </div>
                                <div className="bg-green-500 p-4 rounded-xl">
                                    <CheckCircleIcon className="w-8 h-8 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Inactive Users */}
                        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border-2 border-red-200 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-600 text-sm font-semibold uppercase tracking-wide">Inactive</p>
                                    <p className="text-4xl font-bold text-red-900 mt-2">{stats.inactive}</p>
                                </div>
                                <div className="bg-red-500 p-4 rounded-xl">
                                    <XCircleIcon className="w-8 h-8 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Roles Breakdown */}
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border-2 border-purple-200 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-purple-600 text-sm font-semibold uppercase tracking-wide">By Role</p>
                                <div className="bg-purple-500 p-2 rounded-lg">
                                    <FunnelIcon className="w-5 h-5 text-white" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                {Object.entries(stats.byRole).map(([role, count]) => (
                                    <div key={role} className="flex justify-between items-center text-sm">
                                        <span className="text-purple-700 capitalize">{role.replace('_', ' ')}</span>
                                        <span className="font-bold text-purple-900 bg-purple-200 px-3 py-1 rounded-full">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Search */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Search Users</label>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, or phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                    />
                                </div>
                            </div>

                            {/* Role Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Role</label>
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                >
                                    <option value="all">All Roles</option>
                                    {STAFF_ROLE_OPTIONS.map((role) => (
                                        <option key={role.value} value={role.value}>{role.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status Filter Chips */}
                        <div className="mt-4 flex flex-wrap gap-3">
                            <button
                                onClick={() => setStatusFilter('all')}
                                className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${statusFilter === 'all'
                                    ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                All ({users.length})
                            </button>
                            <button
                                onClick={() => setStatusFilter('active')}
                                className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${statusFilter === 'active'
                                    ? 'bg-green-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Active ({stats.active})
                            </button>
                            <button
                                onClick={() => setStatusFilter('inactive')}
                                className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${statusFilter === 'inactive'
                                    ? 'bg-red-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Inactive ({stats.inactive})
                            </button>
                        </div>
                    </div>

                    {/* Users Table */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">User</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Email</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Last Login</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredUsers.map((user, index) => (
                                        <tr
                                            key={user.id}
                                            className="hover:bg-gradient-to-r hover:from-emerald-50 hover:to-cyan-50 transition-colors duration-150"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-12 w-12 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                                                        {user.full_name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-bold text-gray-900">{user.full_name}</div>
                                                        <div className="text-xs text-gray-500">
                                                            <ClockIcon className="w-3 h-3 inline mr-1" />
                                                            Joined {new Date(user.created_at).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 font-medium">{user.email}</div>
                                                {user.phone && (
                                                    <div className="text-xs text-gray-500 mt-0.5 flex items-center">
                                                        <PhoneIcon className="w-3.5 h-3.5 mr-1 text-gray-400" />
                                                        <span>{user.phone}</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border-2 ${getRoleBadgeColor(user.role)}`}>
                                                    {getRoleIcon(user.role)}
                                                    {user.role.replace('_', ' ').toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${user.is_active
                                                    ? 'bg-green-100 text-green-800 border-2 border-green-200'
                                                    : 'bg-red-100 text-red-800 border-2 border-red-200'
                                                    }`}>
                                                    {user.is_active ? (
                                                        <><CheckCircleIcon className="w-3 h-3 mr-1" /> ACTIVE</>
                                                    ) : (
                                                        <><XCircleIcon className="w-3 h-3 mr-1" /> INACTIVE</>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : (
                                                    <span className="text-gray-400 italic">Never</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => openEditModal(user)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                                    title="Edit user"
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                                    title="Delete user"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {filteredUsers.length === 0 && !fetchError && (
                            <div className="text-center py-12">
                                <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium">No users found matching your criteria</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Invite Link Modal */}
            {showInviteLinkModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl transform animate-slideUp">
                        <div className="border-b border-slate-100 p-8 pb-6">
                            <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-600">Registration invite</p>
                            <h2 className="mt-2 text-3xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                                Create Invite Link
                            </h2>
                            <p className="mt-2 text-sm text-slate-500">
                                Choose the role and dashboard pages first. The invited person only fills their own contact details, then waits for approval.
                            </p>
                        </div>
                        <form onSubmit={handleCreateInviteLink} className="space-y-5 p-8">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Assigned Role</label>
                                <select
                                    value={sanitizeStaffRole(inviteLinkData.role)}
                                    onChange={(e) => {
                                        const role = sanitizeStaffRole(e.target.value);
                                        setInviteLinkData({ role, page_access: rolePageDefaults(role) });
                                    }}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                >
                                    {STAFF_ROLE_OPTIONS.map((role) => (
                                        <option key={role.value} value={role.value}>{role.label}</option>
                                    ))}
                                </select>
                            </div>

                            {sanitizeStaffRole(inviteLinkData.role) === 'farmer' && (
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                                    <p className="flex items-center gap-2 text-sm font-black text-amber-900">
                                        <Squares2X2Icon className="h-5 w-5 text-amber-600" />
                                        Mobile-only access
                                    </p>
                                    <p className="mt-2 text-sm leading-6 text-amber-800">
                                        Farmer accounts do not receive web dashboard access. The selected pages below control what appears in the mobile app drawer and tabs.
                                    </p>
                                </div>
                            )}
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <p className="flex items-center gap-2 text-sm font-black text-slate-900">
                                            <Squares2X2Icon className="h-5 w-5 text-emerald-600" />
                                            {sanitizeStaffRole(inviteLinkData.role) === 'farmer' ? 'Mobile app pages' : 'Dashboard pages'}
                                        </p>
                                        <p className="mt-1 text-xs text-slate-500">
                                            {inviteLinkData.page_access.length} of {inviteAvailablePages.length} pages available to this role selected.
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setInviteLinkData((prev) => ({ ...prev, page_access: rolePageDefaults(prev.role) }))}
                                            className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-black text-emerald-700 hover:bg-emerald-50"
                                        >
                                            Select all
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setInviteLinkData((prev) => ({ ...prev, page_access: rolePageDefaults(prev.role).filter((key) => key === 'dashboard') }))}
                                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                                        >
                                            Dashboard only
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4 grid max-h-64 grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
                                    {inviteAvailablePages.map((page) => {
                                        const checked = inviteLinkData.page_access.includes(page.key);
                                        return (
                                            <button
                                                key={page.key}
                                                type="button"
                                                onClick={() => toggleInvitePageAccess(page.key)}
                                                className={`rounded-xl border p-4 text-left transition ${
                                                    checked
                                                        ? 'border-emerald-300 bg-white shadow-sm ring-2 ring-emerald-100'
                                                        : 'border-slate-200 bg-white/70 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                                        checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {checked && <CheckCircleIcon className="h-4 w-4" />}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-black capitalize text-slate-800">{page.title}</span>
                                                        <span className="mt-1 block text-[11px] font-semibold text-slate-400">{page.key}</span>
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {generatedInviteUrl && (
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                                    <p className="text-sm font-black text-emerald-900">Invite link ready</p>
                                    <div className="mt-3 flex flex-col gap-3 md:flex-row">
                                        <input
                                            readOnly
                                            value={generatedInviteUrl}
                                            className="flex-1 rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard?.writeText(generatedInviteUrl);
                                                alert('Invite link copied.');
                                            }}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white hover:bg-emerald-700"
                                        >
                                            <ClipboardDocumentIcon className="h-5 w-5" />
                                            Copy
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs font-semibold text-emerald-700">
                                        Share this link with the invited user. Their request will appear in Pending Approval after they submit.
                                    </p>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowInviteLinkModal(false)}
                                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    disabled={inviteSubmitting}
                                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-700 shadow-lg hover:shadow-xl disabled:opacity-60"
                                >
                                    {inviteSubmitting ? 'Creating...' : 'Create Link'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create/Edit User Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl transform animate-slideUp">
                        <div className="border-b border-slate-100 p-8 pb-6">
                            <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-600">Tenant access control</p>
                            <h2 className="mt-2 text-3xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                                {editingUser ? 'Edit User Access' : 'Invite User'}
                            </h2>
                            <p className="mt-2 text-sm text-slate-500">
                                Assign role and dashboard pages for this tenant only. Available pages are limited to what Super Admin enabled for the workspace.
                            </p>
                        </div>
                        <div className="p-8">
                        {!editingUser && sanitizeStaffRole(formData.role) !== 'farmer' && (
                            <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                                <p className="text-sm font-bold text-emerald-900">Passwordless account</p>
                                <p className="mt-1 text-xs leading-5 text-emerald-700">
                                    This creates an active staff account. The user signs in with a one-time email code; no password is stored.
                                </p>
                            </div>
                        )}
                        {!editingUser && sanitizeStaffRole(formData.role) === 'farmer' && (
                            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                                <p className="text-sm font-bold text-amber-950">Farmer mobile sign-in</p>
                                <p className="mt-1 text-xs leading-5 text-amber-800">
                                    A phone number is required. Access is created automatically, and the farmer enters that phone number to sign in. Email is optional.
                                </p>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.full_name}
                                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                        required
                                    />
                                </div>
                            {editingUser ? (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                        />
                                        <p className="mt-2 text-xs font-medium text-slate-500">
                                            Email is the login identity. Changing it affects all workspaces.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                                        <input
                                            type="tel"
                                            value={formData.phone || ''}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            placeholder="Optional"
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                        />
                                    </div>
                                </>
                            ) : sanitizeStaffRole(formData.role) === 'farmer' ? (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone number *</label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            placeholder="0788 123 456"
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                            required
                                        />
                                        <p className="mt-2 text-xs font-medium text-amber-700">Used for sign-in. Enter a valid Rwanda number.</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email (optional)</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                            required
                                        />
                                        <p className="mt-2 text-xs font-medium text-emerald-700">
                                            The user will receive an email with the dashboard login link.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone (optional)</label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                        />
                                    </div>
                                </>
                            )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                                <select
                                    value={sanitizeStaffRole(formData.role)}
                                    onChange={(e) => {
                                        const role = sanitizeStaffRole(e.target.value);
                                        setFormData({ ...formData, role });
                                        setSelectedPageKeys(rolePageDefaults(role));
                                    }}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all outline-none"
                                >
                                    {STAFF_ROLE_OPTIONS.map((role) => (
                                        <option key={role.value} value={role.value}>{role.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center space-x-3 bg-gray-50 p-4 rounded-xl">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-5 h-5 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500"
                                />
                                <label className="text-sm font-semibold text-gray-700">Active User</label>
                            </div>
                            {sanitizeStaffRole(formData.role) === 'farmer' && (
                                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                                    <p className="flex items-center gap-2 text-sm font-black text-amber-900">
                                        <Squares2X2Icon className="h-5 w-5 text-amber-600" />
                                        Mobile-only access
                                    </p>
                                    <p className="mt-2 text-sm leading-6 text-amber-800">
                                        Farmer accounts remain mobile-only. The selected pages below decide which mobile tabs, drawer links, and field tools appear.
                                    </p>
                                </div>
                            )}
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <p className="flex items-center gap-2 text-sm font-black text-slate-900">
                                            <Squares2X2Icon className="h-5 w-5 text-emerald-600" />
                                            {sanitizeStaffRole(formData.role) === 'farmer' ? 'Mobile app pages' : 'Dashboard pages'}
                                        </p>
                                        <p className="mt-1 text-xs text-slate-500">
                                            {selectedPageKeys.length} of {roleAvailablePages.length} pages available to this role selected.
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedPageKeys(rolePageDefaults(formData.role))}
                                            className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-black text-emerald-700 hover:bg-emerald-50"
                                        >
                                            Select all
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedPageKeys(rolePageDefaults(formData.role).filter((key) => key === 'dashboard'))}
                                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                                        >
                                            Dashboard only
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4 grid max-h-64 grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
                                    {roleAvailablePages.map((page) => {
                                        const checked = selectedPageKeys.includes(page.key);
                                        return (
                                            <button
                                                key={page.key}
                                                type="button"
                                                onClick={() => togglePageAccess(page.key)}
                                                className={`rounded-xl border p-4 text-left transition ${
                                                    checked
                                                        ? 'border-emerald-300 bg-white shadow-sm ring-2 ring-emerald-100'
                                                        : 'border-slate-200 bg-white/70 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                                        checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {checked && <CheckCircleIcon className="h-4 w-4" />}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-black capitalize text-slate-800">{page.title}</span>
                                                        <span className="mt-1 block text-[11px] font-semibold text-slate-400">{page.key}</span>
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                    {availablePages.length === 0 && (
                                        <div className="col-span-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">
                                            No dashboard pages are currently enabled for this tenant. Ask Super Admin to enable pages first.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8">
                                {editingUser && (
                                    <p className="self-center mr-auto text-xs font-medium text-slate-400">
                                        Role and page access apply only to this workspace. Email and phone update the shared login identity.
                                    </p>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                                >
                                    {editingUser ? 'Update User' : sanitizeStaffRole(formData.role) === 'farmer' ? 'Create Farmer' : 'Create Staff & Send OTP Invite'}
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Approve Modal */}
            {showApproveModal && selectedPendingUser && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl transform animate-slideUp">
                        <h2 className="text-3xl font-bold text-green-600 mb-6">Approve User</h2>
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                            <p className="text-sm font-semibold text-green-900">{selectedPendingUser.full_name}</p>
                            <p className="text-xs text-green-700 mt-1">{selectedPendingUser.email}</p>
                            <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                <ShieldCheckIcon className="w-3.5 h-3.5 mr-1" />
                                OTP-first access
                            </div>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Assign Role</label>
                                <select
                                    value={approvalData.role}
                                    onChange={(e) => setApprovalData({ ...approvalData, role: sanitizeApprovalRole(e.target.value) })}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all outline-none"
                                >
                                    {APPROVAL_ROLE_OPTIONS.map((role) => (
                                        <option key={role.value} value={role.value}>{role.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <p className="text-sm text-slate-600">
                                    Approval activates the account. The user signs in with email or phone OTP.
                                </p>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowApproveModal(false);
                                        setSelectedPendingUser(null);
                                    }}
                                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApprove}
                                    disabled={actionLoading}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                                >
                                    {actionLoading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                                    {actionLoading ? 'Approving...' : 'Approve User'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedPendingUser && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl transform animate-slideUp">
                        <h2 className="text-3xl font-bold text-red-600 mb-6">Reject Request</h2>
                        <div className="mb-6 p-4 bg-red-50 rounded-lg border-2 border-red-200">
                            <p className="text-sm font-semibold text-red-900">{selectedPendingUser.full_name}</p>
                            <p className="text-xs text-red-700 mt-1">{selectedPendingUser.email}</p>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Rejection Reason (Optional)</label>
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-100 focus:border-red-500 transition-all outline-none resize-none"
                                    rows={4}
                                    placeholder="Enter reason for rejection..."
                                />
                            </div>
                            <div className="flex justify-end space-x-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setSelectedPendingUser(null);
                                    }}
                                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={actionLoading}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                                >
                                    {actionLoading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                                    {actionLoading ? 'Rejecting...' : 'Reject Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default UsersPage;
